/**
 * @fileoverview Admin Impersonation Context
 * 
 * Allows admins to "view as" any user, seeing the app from their perspective.
 * The admin's actual session remains intact - this only changes which user's
 * data is fetched and displayed throughout the app.
 * 
 * Security:
 * - Only users with 'admin' role can impersonate
 * - All impersonation events are logged for audit
 * - Clear visual indicator when impersonation is active
 * 
 * @module contexts/ImpersonationContext
 */

import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from './AuthContext';
import { logEventDirect } from '@/hooks/useEventLogger';
import { toast } from 'sonner';

export interface ImpersonatedUser {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
}

export interface ImpersonationContextType {
  /** The user being impersonated (null if not impersonating) */
  impersonatedUser: ImpersonatedUser | null;
  /** Whether impersonation mode is active */
  isImpersonating: boolean;
  /** Start impersonating a user */
  startImpersonation: (userId: string) => Promise<boolean>;
  /** Stop impersonating and return to admin view */
  stopImpersonation: () => void;
  /** Get the effective user ID (impersonated or real) */
  effectiveUserId: string | null;
}

// Export context for use in useAuth hook
export const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

const IMPERSONATION_STORAGE_KEY = 'admin_impersonation';

interface ImpersonationProviderProps {
  children: ReactNode;
}

export function ImpersonationProvider({ children }: ImpersonationProviderProps) {
  const { user: adminUser } = useAuthContext();
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if current user is admin
  useEffect(() => {
    async function checkAdmin() {
      if (!adminUser) {
        setIsAdmin(false);
        setImpersonatedUser(null);
        return;
      }

      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', adminUser.id)
        .eq('role', 'admin')
        .maybeSingle();

      setIsAdmin(!!data);

      // If not admin, clear any impersonation
      if (!data) {
        setImpersonatedUser(null);
        localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      }
    }

    checkAdmin();
  }, [adminUser]);

  // Restore impersonation from localStorage on mount (for page refreshes)
  useEffect(() => {
    if (!isAdmin) return;

    const stored = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // V-019 fix: Validate schema before restoring impersonation state
        if (
          parsed &&
          typeof parsed === 'object' &&
          typeof parsed.id === 'string' &&
          parsed.id.length > 0 &&
          typeof parsed.email === 'string'
        ) {
          setImpersonatedUser({
            id: parsed.id,
            email: parsed.email,
            display_name: typeof parsed.display_name === 'string' ? parsed.display_name : null,
            avatar_url: typeof parsed.avatar_url === 'string' ? parsed.avatar_url : null,
          });
        } else {
          // Invalid shape — discard
          localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
        }
      } catch {
        localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      }
    }
  }, [isAdmin]);

  const startImpersonation = useCallback(async (userId: string): Promise<boolean> => {
    if (!adminUser || !isAdmin) {
      toast.error('You must be an admin to impersonate users');
      return false;
    }

    if (userId === adminUser.id) {
      toast.error("You can't impersonate yourself");
      return false;
    }

    try {
      // Fetch user details
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('user_id, display_name, avatar_url')
        .eq('user_id', userId)
        .single();

      if (error || !profile) {
        toast.error('User not found');
        return false;
      }

      // Fetch user email via edge function (admins only)
      const { data: emailData } = await supabase.functions.invoke('admin-get-user-emails', {
        body: { userIds: [userId] },
      });

      const email = emailData?.emails?.[userId] || 'Unknown email';

      const impersonated: ImpersonatedUser = {
        id: profile.user_id,
        email,
        display_name: profile.display_name,
        avatar_url: profile.avatar_url,
      };

      setImpersonatedUser(impersonated);
      localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(impersonated));

      // Log the impersonation event for audit
      logEventDirect('admin_impersonation_start', 'navigation', {
        impersonated_user_id: userId,
        impersonated_email: email,
      }, adminUser.id);

      toast.success(`Now viewing as ${profile.display_name || email}`);
      return true;
    } catch (err) {
      console.error('Error starting impersonation:', err);
      toast.error('Failed to impersonate user');
      return false;
    }
  }, [adminUser, isAdmin]);

  const stopImpersonation = useCallback(() => {
    if (impersonatedUser && adminUser) {
      // Log the end of impersonation
      logEventDirect('admin_impersonation_end', 'navigation', {
        impersonated_user_id: impersonatedUser.id,
      }, adminUser.id);
    }

    setImpersonatedUser(null);
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    toast.success('Returned to admin view');
  }, [impersonatedUser, adminUser]);

  // The effective user ID is the impersonated user if active, otherwise the real user
  const effectiveUserId = impersonatedUser?.id ?? adminUser?.id ?? null;

  return (
    <ImpersonationContext.Provider
      value={{
        impersonatedUser,
        isImpersonating: !!impersonatedUser,
        startImpersonation,
        stopImpersonation,
        effectiveUserId,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

/**
 * Hook to access impersonation context.
 */
export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
}
